import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, HelpCircle, Languages } from 'lucide-react';
import { useLocalizationQualityReport } from '@/hooks/useAnalyticsReports';
import { ReportFiltersState } from '@/features/reports/components/filters/ReportFilters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { useTheme } from '@/contexts/ThemeContext';

interface GlobalCoverageSummaryCardProps {
  filters: ReportFiltersState;
}

/**
 * Card component that displays global localization coverage summary data
 * Shows translation coverage across all locales for the organization
 */
const GlobalCoverageSummaryCard: React.FC<GlobalCoverageSummaryCardProps> = ({ filters }) => {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();

  const { data, isLoading, error, refetch } = useLocalizationQualityReport();

  // Calculate global localization coverage from all locales with proper null safety
  const localizationData = data || [];

  const averageCoverage =
    localizationData.length > 0
      ? localizationData.reduce((sum, locale) => sum + (locale.translation_coverage || 0), 0) /
        localizationData.length
      : 0;

  const totalAttributes =
    localizationData.length > 0
      ? localizationData.reduce((sum, locale) => sum + (locale.total_attributes || 0), 0)
      : 0;

  const translatedAttributes =
    localizationData.length > 0
      ? localizationData.reduce((sum, locale) => sum + (locale.translated_attributes || 0), 0)
      : 0;

  // Calculate missing attributes
  const missingAttributes = totalAttributes - translatedAttributes;
  const activeLocales = localizationData.length;

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Languages className="size-4" />
            Global Localization Coverage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-8 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Languages className="size-4" />
            Global Localization Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Error Loading Data</AlertTitle>
            <AlertDescription>
              Failed to load localization coverage data. Please try again.
            </AlertDescription>
          </Alert>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
            <RefreshCw className="mr-2 size-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Languages className="size-4" />
          Global Localization Coverage
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle
                  className={`size-4 ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Average translation coverage across all active locales. Shows how well your
                  product attributes are translated.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <CardDescription>
          Translation completeness across {activeLocales} active locale
          {activeLocales !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main localization coverage progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Average Translation Coverage</span>
            <span className="font-medium">{Math.round(averageCoverage)}%</span>
          </div>
          <Progress value={averageCoverage} className="h-2" />
        </div>

        {/* Attribute statistics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className={`text-sm ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Translated Attributes
            </p>
            <p className="text-2xl font-bold text-green-600">
              {translatedAttributes.toLocaleString()}
            </p>
          </div>
          <div className="space-y-1">
            <p className={`text-sm ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Missing Translations
            </p>
            <p className="text-2xl font-bold text-red-600">{missingAttributes.toLocaleString()}</p>
          </div>
        </div>

        {/* Locale and total attributes info */}
        <div className="space-y-2 border-t pt-2">
          <div
            className={`flex items-center justify-between text-sm ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
          >
            <span>Active Locales</span>
            <span className="font-medium">{activeLocales}</span>
          </div>
          <div
            className={`flex items-center justify-between text-sm ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
          >
            <span>Total Expected Attributes</span>
            <span className="font-medium">{totalAttributes.toLocaleString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GlobalCoverageSummaryCard;
